import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject, finalize, takeUntil, from, concatMap, toArray } from 'rxjs';
import { ObservationReferenceRange, ObservationReferenceRangeService, ObservationReferenceRangeTemplate } from './observation-reference-range.service';
import { ReferenceRangeImportExportService } from './reference-range-import-export.service';
import { ObservationReferenceRangesDialogComponent } from './observation-reference-ranges-dialog.component';

@Component({
  selector: 'app-observation-reference-ranges',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTableModule,
    MatTooltipModule
  ],
  templateUrl: './observation-reference-ranges.component.html',
  styleUrls: ['./observation-reference-ranges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationReferenceRangesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  templates: ObservationReferenceRangeTemplate[] = [];
  loadingTemplates = false;

  readonly displayedColumns = ['code', 'range', 'appliesTo', 'age', 'updated', 'actions'];

  constructor(
    private readonly referenceRangesService: ObservationReferenceRangeService,
    private readonly importExportService: ReferenceRangeImportExportService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTemplates(): void {
    this.loadingTemplates = true;
    this.referenceRangesService
      .listTemplates()
      .pipe(
        finalize(() => this.loadingTemplates = false),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: templates => {
          this.templates = templates ?? [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.templates = [];
          this.showMessage('Unable to load saved reference ranges.', 'error');
          this.cdr.markForCheck();
        }
      });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(ObservationReferenceRangesDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.loadTemplates();
      }
    });
  }

  editTemplate(template: ObservationReferenceRangeTemplate): void {
    const dialogRef = this.dialog.open(ObservationReferenceRangesDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      disableClose: true,
      data: { template }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.loadTemplates();
      }
    });
  }

  deleteTemplate(template: ObservationReferenceRangeTemplate): void {
    if (!template.id) {
      return;
    }
    const confirmed = window.confirm('Delete this reference range template?');
    if (!confirmed) {
      return;
    }
    this.referenceRangesService.deleteTemplate(template.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.showMessage('Reference range deleted.', 'success');
        this.loadTemplates();
        this.cdr.markForCheck();
      },
      error: () => {
        this.showMessage('Failed to delete reference range.', 'error');
        this.cdr.markForCheck();
      }
    });
  }

  rangeSummary(range?: ObservationReferenceRange): string {
    if (!range) {
      return '—';
    }
    const lowStr = range.low?.value != null ? `${range.low.value} ${range.low.unit || range.low.code || ''}`.trim() : null;
    const highStr = range.high?.value != null ? `${range.high.value} ${range.high.unit || range.high.code || ''}`.trim() : null;
    
    if (lowStr && highStr) {
      return `${lowStr} - ${highStr}`;
    } else if (lowStr) {
      return `>= ${lowStr}`;
    } else if (highStr) {
      return `<= ${highStr}`;
    } else if (range.text) {
      return range.text;
    }
    return 'Configured';
  }

  appliesToSummary(range?: ObservationReferenceRange): string {
    const applies = range?.appliesTo ?? [];
    if (!applies.length) {
      return 'All patients';
    }
    return applies
      .map(concept => concept.text || concept.coding?.[0]?.display || concept.coding?.[0]?.code || 'Unknown')
      .join(', ');
  }

  ageSummary(range?: ObservationReferenceRange): string {
    return this.referenceRangesService.describeAge(range);
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const result = await this.importExportService.parseExcel(file);
      if (result.errors.length) {
        this.showMessage(`Excel Error: ${result.errors[0]}`, 'error');
      } else if (result.templates.length) {
        // 1. In-memory deduplication (prevents saving duplicate rows from the Excel itself)
        const uniqueTemplates: ObservationReferenceRangeTemplate[] = [];
        for (const t of result.templates) {
          const isDuplicate = uniqueTemplates.some(u => this.referenceRangesService.isTemplateDuplicate(u, t));
          if (!isDuplicate) {
            uniqueTemplates.push(t);
          } else {
            console.log(`[Deduplication - In Memory] Skipping duplicate row from Excel for code: ${t.code?.code}`, t);
          }
        }

        // 2. Sequential saving (prevents race conditions with backend DB deduplication)
        from(uniqueTemplates).pipe(
          concatMap(t => this.referenceRangesService.saveTemplate(t)),
          toArray(),
          takeUntil(this.destroy$)
        ).subscribe((results: any[]) => {
          this.loadTemplates();
          const skipped = results.filter(r => r._skippedDuplicate).length;
          const saved = results.length - skipped;
          
          if (skipped > 0) {
            this.showMessage(`Import complete! Saved: ${saved}, Skipped DB Duplicates: ${skipped}`, 'success');
          } else {
            this.showMessage(`Successfully imported ${saved} unique ranges!`, 'success');
          }
        });
      }
    } catch (e) {
      this.showMessage(`Error parsing Excel: ${String(e)}`, 'error');
    }
    
    if (input) {
      input.value = '';
    }
  }

  downloadTemplateExcel(): void {
    this.importExportService.downloadExcelTemplate();
    this.showMessage('Template Excel downloaded. Fill it and upload to bulk-import ranges.', 'success');
  }

  downloadCommonTemplateExcel(): void {
    this.importExportService.downloadCommonExcelTemplate();
    this.showMessage('Common template Excel downloaded.', 'success');
  }

  downloadTemplatesAsExcel(): void {
    if (!this.templates.length) {
      this.showMessage('No templates to export.', 'error');
      return;
    }
    this.importExportService.downloadTemplatesAsExcel(this.templates);
    this.showMessage(`Exported ${this.templates.length} templates to Excel.`, 'success');
  }

  private showMessage(message: string, severity?: 'error' | 'success'): void {
    const config: any = { duration: 3500 };
    if (severity === 'error') {
      config.panelClass = 'snackbar-error';
    } else if (severity === 'success') {
      config.panelClass = 'snackbar-success';
    }
    this.snackBar.open(message, 'Dismiss', config);
  }
}
